import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSort } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTooltip } from '@angular/material/tooltip'
import {
  CollectionUtil,
  ComparatorBuilder,
  ElderDataToolbarComponent,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { ProcessingTimeSpec } from '../processing-time-spec'
import {
  IProcessingTimeSpecDialogOptions,
  ProcessingTimeSpecDetailDialogComponent,
} from '../processing-time-spec-detail-dialog/processing-time-spec-detail-dialog.component'
import { ProcessingTimeUtils } from '../processing-time-utils'

@Component({
  selector: 'app-processing-time-spec-table',
  templateUrl: './processing-time-spec-table.component.html',
  styleUrls: ['./processing-time-spec-table.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatTooltip,
    MatIconButton,
    ElderStopEventPropagationDirective,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ProcessingTimeSpecTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _pTimeSpecs$ = new BehaviorSubject<ProcessingTimeSpec[]>([])

  @Output()
  public variantSetsChange = new EventEmitter<ProcessingTimeSpec[][]>()

  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get pTimeSpecs$(): Observable<ProcessingTimeSpec[]> {
    return this._pTimeSpecs$.asObservable()
  }

  public get pTimeSpecs(): ProcessingTimeSpec[] {
    return this._pTimeSpecs$.getValue()
  }

  public set pTimeSpecs(pTimes: ProcessingTimeSpec[]) {
    this._pTimeSpecs$.next(pTimes)
  }

  @Input()
  public set variantSets(variantSets: ProcessingTimeSpec[][]) {
    const pTimes = this.flattenAndEnumerate(variantSets)
    pTimes.sort(ComparatorBuilder.fieldSort('setNumber'))
    this.pTimeSpecs = pTimes
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onClick(spec: ProcessingTimeSpec): void {
    const dialogRef = this.openDialog(spec)
    dialogRef.afterClosed().subscribe((pTime: ProcessingTimeSpec) => {
      if (pTime) {
        const pTimeToUpdate = this.pTimeSpecs.find((p) => p.id === pTime.id)
        ProcessingTimeUtils.update(pTime, pTimeToUpdate)
        this.emitCurrentState()
      }
    })
  }

  public onNew(event: Event): void {
    const newPTime = new ProcessingTimeSpec()
    newPTime.setNumber = this.getNextSetNumber()
    const dialogRef = this.openDialog(newPTime)

    dialogRef.afterClosed().subscribe((spec: ProcessingTimeSpec) => {
      if (spec) {
        this.addConstraint(spec)
      }
    })
  }

  public onRemove(specs: ProcessingTimeSpec[]): void {
    this.removeConstraints(specs)
  }

  public moveUp(spec: ProcessingTimeSpec): void {
    const index = this.pTimeSpecs.findIndex((s) => s.id === spec.id)
    const previousIndex = index - 1
    const previous = previousIndex >= 0 ? this.pTimeSpecs[previousIndex] : null

    if (previous) {
      if (previous.setNumber === spec.setNumber) {
        this.swap(this.pTimeSpecs, index, previousIndex)
      } else {
        spec.setNumber = previous.setNumber
      }
    }

    this.emitCurrentState()
  }

  public moveDown(spec: ProcessingTimeSpec): void {
    const index = this.pTimeSpecs.findIndex((s) => s.id === spec.id)
    const nextIndex = index + 1
    const next = this.pTimeSpecs.length > nextIndex ? this.pTimeSpecs[nextIndex] : null

    if (next) {
      if (next.setNumber === spec.setNumber) {
        this.swap(this.pTimeSpecs, index, nextIndex)
      } else {
        spec.setNumber = next.setNumber
      }
    }

    this.emitCurrentState()
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private addConstraint(toAdd: ProcessingTimeSpec) {
    const specs = [...this.pTimeSpecs]
    specs.push(toAdd)
    this.pTimeSpecs = specs
    this.emitCurrentState()
  }

  private removeConstraints(toRemove: ProcessingTimeSpec[]) {
    const specs = [...this.pTimeSpecs]
    toRemove.forEach((c) => specs.splice(specs.indexOf(c), 1))
    this.pTimeSpecs = specs
    this.emitCurrentState()
  }

  private swap(array: any[], indexA: number, indexB: number): void {
    const temp = array[indexA]
    array[indexA] = array[indexB]
    array[indexB] = temp
  }

  private emitCurrentState(): void {
    this.variantSetsChange.emit(this.group(this.pTimeSpecs))
  }

  private getNextSetNumber(): number {
    return (
      Math.max.apply(
        Math,
        this.pTimeSpecs.map((p) => p.setNumber)
      ) + 1
    )
  }

  private flattenAndEnumerate(variantSets: ProcessingTimeSpec[][]): ProcessingTimeSpec[] {
    const pTimes: ProcessingTimeSpec[] = []
    if (variantSets) {
      for (let i = 0; i < variantSets.length; i++) {
        variantSets[i].forEach((p) => (p.setNumber = i + 1))
      }

      variantSets.forEach((vs) => pTimes.push(...vs))
    }

    return pTimes
  }

  private group(pTimes: ProcessingTimeSpec[]): ProcessingTimeSpec[][] {
    const variantSets: ProcessingTimeSpec[][] = []
    const grouped = CollectionUtil.groupByKey(pTimes, (pTime) => pTime.setNumber)
    grouped.forEach((value) => variantSets.push(value))
    return variantSets
  }

  private openDialog(
    spec: ProcessingTimeSpec
  ): MatDialogRef<ProcessingTimeSpecDetailDialogComponent> {
    return this.dialog.open(ProcessingTimeSpecDetailDialogComponent, {
      minWidth: '35vw',
      minHeight: '35vh',
      data: <IProcessingTimeSpecDialogOptions>{
        pTime: spec,
      },
    })
  }
}
